import { Injectable } from '@angular/core';

import { Logger } from '../../common/logging/logger';
import { AccountService } from '../../shared/services/account.service';
import { ConnectionStateService, Connectivity } from '../../shared/services/connection-state.service';
import { AuthClient } from '../auth-client';
import { SessionController } from './session-controller';
import { logSessionEvent } from './session-log';
import { SessionState } from './session-state-types';

/** A session that has not left `starting` after this long is stuck: the user sees only the splash screen. */
const STUCK_STARTING_MS = 15_000;
/** `degraded` lasting this long is a backend problem worth an event, not a hiccup. */
const DEGRADED_LONG_MS = 5 * 60_000;

/**
 * Keeps Sentry informed about the session without the session knowing about Sentry (#27294).
 *
 * Every event Sentry receives — from the session or from anywhere else in the app — carries the
 * tags `session.state`, `connectivity` and `token.state` as they were at that moment, and the
 * user of the current account. The two timers report the conditions a user would describe as
 * "the app does not start" and "the app has been reconnecting for ages"; the events are sent once
 * per process (see `logSessionEvent`). Diagnostics only: every handler is guarded, so a failing
 * Sentry call can never reach the session.
 */
@Injectable({ providedIn: 'root' })
export class SessionTelemetry {
  private _degradedSince: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly _session: SessionController,
    private readonly _connectivity: ConnectionStateService,
    private readonly _accountService: AccountService,
    private readonly _oauth: AuthClient,
    private readonly _logger: Logger
  ) {
    this._session.state$.subscribe(state => this.guarded(() => this.onState(state)));
    this._connectivity.state$.subscribe(connectivity =>
      this.guarded(() => this._logger.setTag('connectivity', describeConnectivity(connectivity)))
    );
    this._accountService.account$.subscribe(account =>
      this.guarded(() =>
        this._logger.setUser(
          account
            ? { id: account._id, email: account.email, username: `${account.lastName} ${account.firstName}` }
            : null
        )
      )
    );
    setTimeout(() => this.guarded(() => this.reportIfStuckStarting()), STUCK_STARTING_MS);
  }

  private onState(state: SessionState): void {
    this._logger.setTag('session.state', state);
    this._logger.setTag('token.state', this._oauth.tokenState);
    if (state === 'degraded') {
      this._degradedSince ??= setTimeout(() => this.guarded(() => this.reportDegradedLong()), DEGRADED_LONG_MS);
      return;
    }
    if (this._degradedSince != null) {
      clearTimeout(this._degradedSince);
      this._degradedSince = null;
    }
  }

  private reportIfStuckStarting(): void {
    if (this._session.state === 'starting') {
      logSessionEvent(
        'stuck_starting',
        describeConnectivity(this._connectivity.state),
        `still starting after ${STUCK_STARTING_MS / 1000} s`
      );
    }
  }

  private reportDegradedLong(): void {
    this._degradedSince = null;
    if (this._session.state === 'degraded') {
      logSessionEvent(
        'degraded_long',
        describeConnectivity(this._connectivity.state),
        `degraded for ${DEGRADED_LONG_MS / 60_000} min`
      );
    }
  }

  private guarded(action: () => void): void {
    try {
      action();
    } catch (error) {
      console.warn('[SessionTelemetry] ignored', error);
    }
  }
}

/** The connectivity as one word for a tag. */
export function describeConnectivity(connectivity: Connectivity): 'online' | 'maintenance' | 'socket-down' | 'unknown' {
  if (connectivity.maintenance) {
    return 'maintenance';
  }
  if (connectivity.socket === 'connected') {
    return 'online';
  }
  return connectivity.socket === 'unknown' ? 'unknown' : 'socket-down';
}
